import { listenSafe } from '../utils/safeTauriEvent';

/**
 * One Tauri listener for `terminal-output` / `terminal-exit`, shared by every
 * local pane. Before this, each terminal registered its own pair and filtered
 * by id — with eight panes every output chunk ran eight callbacks.
 *
 * Also closes a startup race the per-pane listeners had: the session id is
 * only known once `terminal_create` resolves, and a fast program (an agent
 * that fails at once, a shell's first prompt) can emit before that. Events
 * for an id nobody has subscribed yet are held briefly and replayed to the
 * first subscriber.
 */
const ORPHAN_TTL_MS = 5000;
const ORPHAN_MAX_CHUNKS = 256;

const subs = new Map(); // sessionId -> { onData, onExit }
const orphans = new Map(); // sessionId -> { at, chunks: string[], exit?: { code } }
let unlisten = null;
let starting = null;

function pruneOrphans(now) {
  orphans.forEach((o, id) => {
    if (now - o.at > ORPHAN_TTL_MS) orphans.delete(id);
  });
}

function orphan(id) {
  let o = orphans.get(id);
  if (!o) {
    o = { at: Date.now(), chunks: [] };
    orphans.set(id, o);
  }
  return o;
}

function onOutput(event) {
  const p = event?.payload;
  if (!p?.id || !p.data) return;
  const sub = subs.get(p.id);
  if (sub) {
    sub.onData?.(p.data);
    return;
  }
  pruneOrphans(Date.now());
  const o = orphan(p.id);
  if (o.chunks.length < ORPHAN_MAX_CHUNKS) o.chunks.push(p.data);
}

function onExit(event) {
  const p = event?.payload;
  if (!p?.id) return;
  const code = typeof p.code === 'number' ? p.code : null;
  const sub = subs.get(p.id);
  if (sub) {
    sub.onExit?.(code);
    return;
  }
  orphan(p.id).exit = { code };
}

let holders = 0;

function startBus() {
  if (unlisten || starting) return starting || Promise.resolve();
  starting = Promise.all([
    listenSafe('terminal-output', onOutput),
    listenSafe('terminal-exit', onExit),
  ]).then(([a, b]) => {
    starting = null;
    unlisten = () => {
      a();
      b();
    };
    // Everyone left while we were registering.
    if (holders === 0) stopBus();
  });
  return starting;
}

function stopBus() {
  if (unlisten) unlisten();
  unlisten = null;
  orphans.clear();
}

/**
 * Hold the bus open (one per mounted terminal). Resolves once both Tauri
 * listeners are registered — await it BEFORE `terminal_create`, so nothing
 * the new session prints can be missed. Returns the release function; the
 * last release removes the listeners.
 */
export async function acquireTerminalBus() {
  holders += 1;
  await startBus();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders === 0 && !starting) stopBus();
  };
}

/**
 * Route one session's events to `handlers` ({ onData(str), onExit(code) }),
 * replaying anything that arrived for `sessionId` before this call. Returns
 * the unsubscribe function.
 */
export function subscribeTerminal(sessionId, handlers) {
  subs.set(sessionId, handlers);
  const o = orphans.get(sessionId);
  if (o) {
    orphans.delete(sessionId);
    o.chunks.forEach((c) => handlers.onData?.(c));
    if (o.exit) handlers.onExit?.(o.exit.code);
  }
  return () => {
    if (subs.get(sessionId) === handlers) subs.delete(sessionId);
  };
}

/** Test-only view of the bus. */
export const __busState = () => ({ holders, subscribers: subs.size, listening: !!unlisten, orphans: orphans.size });
